import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput,
  Typography
} from '@mui/material';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import dayjs from 'dayjs';
import useNow from '@hooks/use-now.ts';
import { useTranslation } from 'react-i18next';
import { ICONS } from '@components/icons/icon.tsx';
import AppAutocompleteV2 from '@components/app-autocomplete/app-autocomplete-v2.tsx';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import { validateNotOnlySpaces } from '@utils/trim-form-values-utils.ts';
import useGroupSettingsStore from '@modules/digital-signage/components/group-settings/store/group-settings.store.ts';
import {
  ScheduleDialogFormProps,
  ScheduleFormValues
} from '@modules/digital-signage/model/playlist-schedule.model.ts';
import {
  buildScheduleOccupancy,
  validateSchedule
} from '@modules/digital-signage/utils/schedule-occupancy.utils.ts';
import {
  createScheduleRule,
  mapFormToScheduleReq,
  mapScheduleToForm
} from '@modules/digital-signage/utils/schedule-form.utils.ts';
import { getSavedSlotKeys } from '@modules/digital-signage/utils/schedule-slots.utils.ts';
import {
  getScheduleMinDate,
  isSavedScheduleEnded
} from '@modules/digital-signage/utils/schedule-date.utils.ts';
import { scheduleErrorMessages } from '@modules/digital-signage/utils/schedule.utils.ts';
import ScheduleRuleCard from '@modules/digital-signage/components/group-settings/components/playlist-schedule/components/schedule-rule-card/schedule-rule-card.tsx';
import ScheduleDatePicker from '@modules/digital-signage/components/group-settings/components/playlist-schedule/components/schedule-dialog/components/schedule-date-picker/schedule-date-picker.tsx';
import ScheduleLegendHeader from '@modules/digital-signage/components/group-settings/components/playlist-schedule/components/schedule-dialog/components/schedule-legend-header/schedule-legend-header.tsx';

const ScheduleDialogForm: React.FC<ScheduleDialogFormProps> = ({
  selectedItem,
  onSave,
  onCancel
}) => {
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const [schedules] = useState(
    () => useGroupSettingsStore.getState().schedules
  );
  const { playlistsOptions, isPlaylistsLoading, groupDetails } =
    useGroupSettingsStore();

  const defaultValues = useMemo(
    () => mapScheduleToForm(selectedItem, playlistsOptions),
    // Options only resolve the initial playlist, they must not reset the form.
    [selectedItem]
  );

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid }
  } = useForm<ScheduleFormValues>({ mode: 'onTouched', defaultValues });

  const { fields, append, remove } = useFieldArray({ control, name: 'rules' });
  const [startDate, endDate, rules] = useWatch({
    control,
    name: ['startDate', 'endDate', 'rules']
  });

  const addRuleButtonRef = useRef<HTMLButtonElement>(null);
  const shouldScrollToNewRule = useRef<boolean>(false);

  useEffect(() => {
    if (!shouldScrollToNewRule.current) return;
    shouldScrollToNewRule.current = false;
    const frame = requestAnimationFrame(() =>
      addRuleButtonRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      })
    );
    return () => cancelAnimationFrame(frame);
  }, [fields.length]);

  const handleAddRule = () => {
    shouldScrollToNewRule.current = true;
    append(createScheduleRule());
  };

  const now = useNow(60000);
  const occupancy = useMemo(
    () =>
      buildScheduleOccupancy(
        { startDate, endDate, rules },
        schedules,
        selectedItem?._id,
        defaultValues,
        dayjs(now)
      ),
    [startDate, endDate, rules, schedules, selectedItem, defaultValues, now]
  );
  const savedSlotKeys = useMemo(
    () =>
      getSavedSlotKeys(
        groupDetails?.playlistSchedules?.find(
          (schedule) => schedule._id === selectedItem?._id
        ),
        {
          start: startDate?.isValid() ? startDate.format('YYYY-MM-DD') : null,
          end: endDate?.isValid() ? endDate.format('YYYY-MM-DD') : null
        }
      ),
    [groupDetails, selectedItem, startDate, endDate]
  );
  const validation = useMemo(
    () => validateSchedule(rules, occupancy, savedSlotKeys),
    [rules, occupancy, savedSlotKeys]
  );

  const selectedPlaylistList = useMemo(
    () => (defaultValues.playlist ? [defaultValues.playlist] : []),
    [defaultValues]
  );

  const isEnded = isSavedScheduleEnded(endDate, defaultValues.endDate);
  const canSave = isValid && validation.isValid && !isEnded;

  const onSubmit = (values: ScheduleFormValues) => {
    const freshValidation = validateSchedule(
      values.rules,
      buildScheduleOccupancy(
        values,
        schedules,
        selectedItem?._id,
        defaultValues,
        dayjs()
      ),
      savedSlotKeys
    );
    if (!freshValidation.isValid) return;
    onSave(mapFormToScheduleReq(values));
  };

  // No past dates, except keeping the saved ones; end can't precede start.
  const dateLimits = useMemo(() => {
    const minStart = getScheduleMinDate(defaultValues.startDate);
    const minEnd = getScheduleMinDate(defaultValues.endDate);
    return {
      startDate: { min: minStart, max: endDate ?? undefined },
      endDate: {
        min: startDate?.isAfter(minEnd) ? startDate : minEnd,
        max: undefined
      }
    };
  }, [defaultValues, startDate, endDate]);

  // The dialog sits inside the GroupSettings form in the React tree, so a
  // submit event must not bubble up to it.
  return (
    <form
      className="flex flex-col gap-3 min-h-full"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <div className="grid flex-1 grid-cols-[300px_1fr] gap-6 max-lg:grid-cols-1">
        <div className="flex flex-col gap-0.5 min-w-0 sticky top-0 self-start max-lg:static">
          <FormControl fullWidth error={!!errors.name}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_digital_signage_schedule_name')}
            </FormLabel>
            <OutlinedInput
              {...register('name', {
                required: true,
                validate: { notOnlySpaces: validateNotOnlySpaces }
              })}
              error={!!errors.name}
              placeholder={t('placeholder_digital_signage_schedule_name')}
            />
            <FormHelperText>
              {errors.name &&
                getErrorMessage({
                  errors,
                  fieldName: 'name',
                  errorMessages: scheduleErrorMessages
                })}
            </FormHelperText>
          </FormControl>

          <AppAutocompleteV2
            name="playlist"
            control={control}
            label={
              <span className="flex items-center">
                <span className="reuqired-symbole">*</span>
                <Typography
                  component="span"
                  className="text-[length:inherit]! text-inherit! font-[inherit]! leading-[inherit]!"
                >
                  {t('label_digital_signage_schedule_playlist')}
                </Typography>
              </span>
            }
            options={playlistsOptions}
            selectedList={selectedPlaylistList}
            placeholder={t('placeholder_digital_signage_schedule_playlist')}
            loading={isPlaylistsLoading()}
            validationProps={{
              errors,
              errorMessages: scheduleErrorMessages,
              getErrorMessage,
              validationRules: { required: true }
            }}
          />

          <ScheduleDatePicker
            name="startDate"
            label="label_digital_signage_schedule_start_date"
            control={control}
            errors={errors}
            dateError={validation.dateError}
            minDate={dateLimits.startDate.min}
            maxDate={dateLimits.startDate.max}
          />
          <ScheduleDatePicker
            name="endDate"
            label="label_digital_signage_schedule_end_date"
            control={control}
            errors={errors}
            dateError={validation.dateError}
            minDate={dateLimits.endDate.min}
            maxDate={dateLimits.endDate.max}
          />
        </div>

        <div className="flex flex-col gap-3 min-w-0 ps-6 border-s border-(--gray-9) max-lg:ps-0 max-lg:border-s-0">
          <ScheduleLegendHeader />

          {isEnded && (
            <Alert severity="info" className="rounded-xl!">
              {t('digital_signage_schedule_ended_banner', {
                date: endDate?.format('MMM D, YYYY')
              })}
            </Alert>
          )}

          <div className="flex flex-col gap-3">
            {fields.map((field, index) => (
              <ScheduleRuleCard
                key={field.id}
                index={index}
                rule={rules[index] ?? field}
                occupancy={occupancy}
                validation={validation.rules[index]}
                canDelete={fields.length > 1}
                showIndex={fields.length > 1}
                disabled={isEnded}
                savedSlotKeys={savedSlotKeys}
                onDaysChange={(days) =>
                  setValue(`rules.${index}.days`, days, { shouldDirty: true })
                }
                onRangesChange={(ranges) =>
                  setValue(`rules.${index}.timeRanges`, ranges, {
                    shouldDirty: true
                  })
                }
                onDelete={() => remove(index)}
              />
            ))}
          </div>

          <Button
            ref={addRuleButtonRef}
            variant="outlined"
            startIcon={ICONS.plusIcon}
            disabled={isEnded}
            onClick={handleAddRule}
            className={`self-end w-full max-w-80! rounded-xl! border-dashed! scroll-mb-24 ${
              isEnded
                ? 'border-(--gray-7)! text-(--gray-3)! [&_svg_path]:stroke-(--gray-3)!'
                : 'border-(--primary)! text-(--primary)! [&_svg_path]:stroke-(--primary)!'
            }`}
          >
            {t('digital_signage_schedule_add_rule')}
          </Button>
        </div>
      </div>

      <div className="flex justify-end sticky bottom-0 mt-1 bg-(--white) pt-3 pb-1">
        <div className="flex gap-3">
          <Button
            variant="outlined"
            onClick={onCancel}
            className="border-(--gray-6)!"
          >
            {t('delete_popup_cancel_button_text')}
          </Button>
          <Button
            variant="contained"
            disabled={!canSave}
            onClick={handleSubmit(onSubmit)}
          >
            {t('save')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ScheduleDialogForm;
